import OutlineTitle from "@/components/OutlineTitle";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { userAPI, authAPI, forumProfileAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, MessageSquare, BellOff, Clock, Wrench, Tag, Gauge, Trophy, Handshake, Sun, Moon } from "lucide-react";
import { TIMEZONE_OPTIONS, tzLabel, DEFAULT_DISPLAY_TZ } from "@/lib/datetime";
import { useTheme } from "@/context/ThemeContext";

const FORUM_BADGE_META = [
  { key: "setup", label: "Rig setup", icon: Wrench },
  { key: "listings", label: "My listings", icon: Tag },
  { key: "simhub", label: "Public SimHub sessions", icon: Gauge },
  { key: "sponsors", label: "Sponsors", icon: Handshake },
  { key: "achievements", label: "Achievements", icon: Trophy },
];

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [allowDM, setAllowDM] = useState(user?.allow_direct_messages ?? true);
  const [muteShareRequests, setMuteShareRequests] = useState(user?.mute_share_requests ?? false);
  const [timezone, setTimezone] = useState(user?.timezone || DEFAULT_DISPLAY_TZ);
  const [forumBadges, setForumBadges] = useState({ setup: true, listings: true, simhub: true, sponsors: true, achievements: true });
  const [achievements, setAchievements] = useState([]);
  const [hiddenAch, setHiddenAch] = useState([]);

  useEffect(() => {
    forumProfileAPI.getMySettings()
      .then((r) => { setForumBadges(r.data.forum_badges || {}); setHiddenAch(r.data.hidden_achievements || []); })
      .catch(() => {});
    forumProfileAPI.getMyAchievements()
      .then((r) => setAchievements(r.data.achievements || []))
      .catch(() => {});
  }, []);

  const toggleForumBadge = async (key, val) => {
    const next = { ...forumBadges, [key]: val };
    setForumBadges(next);
    try { await forumProfileAPI.updateMySettings({ forum_badges: next }); toast.success("Forum settings updated"); }
    catch { setForumBadges(forumBadges); toast.error("Could not update settings"); }
  };

  const toggleAchievementVisible = async (achId, visible) => {
    const next = visible ? hiddenAch.filter((x) => x !== achId) : [...new Set([...hiddenAch, achId])];
    setHiddenAch(next);
    try { await forumProfileAPI.updateMySettings({ hidden_achievements: next }); }
    catch { setHiddenAch(hiddenAch); toast.error("Could not update settings"); }
  };

  const tzOptions = TIMEZONE_OPTIONS.some((o) => o.value === timezone)
    ? TIMEZONE_OPTIONS
    : [{ value: timezone, label: tzLabel(timezone) }, ...TIMEZONE_OPTIONS];

  const changeTimezone = async (tz) => {
    const prev = timezone;
    setTimezone(tz);
    try {
      const res = await authAPI.updateProfile({ timezone: tz });
      const updated = { ...user, ...res.data, timezone: tz };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
      toast.success("Time zone updated");
    } catch {
      setTimezone(prev);
      toast.error("Could not update time zone");
    }
  };

  const toggleDM = async (val) => {
    setAllowDM(val);
    try {
      await userAPI.updateSettings(val);
      const updated = { ...user, allow_direct_messages: val };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
      toast.success("Settings updated");
    } catch {
      setAllowDM(!val);
      toast.error("Could not update settings");
    }
  };

  const toggleMuteShareRequests = async (val) => {
    setMuteShareRequests(val);
    try {
      await userAPI.updateMuteShareRequests(val);
      const updated = { ...user, mute_share_requests: val };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
      toast.success("Settings updated");
    } catch {
      setMuteShareRequests(!val);
      toast.error("Could not update settings");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <OutlineTitle>Settings</OutlineTitle>

      <Card className="border-border bg-card p-5">
        <h2 className="mb-4 font-semibold">Appearance</h2>
        <div className="flex items-center justify-between rounded-md border border-border p-4">
          <div className="flex items-center gap-3">
            {isDark ? <Moon className="h-5 w-5 text-muted-foreground" /> : <Sun className="h-5 w-5 text-muted-foreground" />}
            <div>
              <Label htmlFor="theme">Theme</Label>
              <p className="text-xs text-muted-foreground">{isDark ? "Dark mode" : "Light mode"}</p>
            </div>
          </div>
          <Switch id="theme" checked={!isDark} onCheckedChange={toggleTheme} data-testid="settings-theme-switch" />
        </div>
      </Card>

      <Card className="border-border bg-card p-5">
        <h2 className="mb-4 font-semibold">Privacy</h2>
        <div className="flex items-center justify-between rounded-md border border-border p-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-primary" />
            <div>
              <Label htmlFor="dm">Allow direct messages</Label>
              <p className="text-xs text-muted-foreground">Let other racers message you directly</p>
            </div>
          </div>
          <Switch id="dm" checked={allowDM} onCheckedChange={toggleDM} data-testid="settings-dm-switch" />
        </div>
      </Card>

      <Card className="border-border bg-card p-5">
        <h2 className="mb-4 font-semibold">SimHub</h2>
        <div className="flex items-center justify-between rounded-md border border-border p-4">
          <div className="flex items-center gap-3">
            <BellOff className="h-5 w-5 text-primary" />
            <div>
              <Label htmlFor="mute-shares">Mute session share request notifications</Label>
              <p className="text-xs text-muted-foreground">Requests still appear in your SimHub inbox, just without a push notification.</p>
            </div>
          </div>
          <Switch id="mute-shares" checked={muteShareRequests} onCheckedChange={toggleMuteShareRequests} data-testid="settings-mute-shares-switch" />
        </div>
      </Card>

      <Card className="border-border bg-card p-5">
        <h2 className="mb-4 font-semibold">Preferences</h2>
        <div className="flex items-center justify-between gap-4 rounded-md border border-border p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-primary" />
            <div>
              <Label>Time zone</Label>
              <p className="text-xs text-muted-foreground">Dates &amp; times display in this zone</p>
            </div>
          </div>
          <Select value={timezone} onValueChange={changeTimezone}>
            <SelectTrigger className="w-[220px]" data-testid="settings-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {tzOptions.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-border bg-card p-5">
        <h2 className="mb-1 font-semibold">Forum identity</h2>
        <p className="mb-4 text-xs text-muted-foreground">Choose which icons appear next to your name on forum posts.</p>
        <div className="space-y-2">
          {FORUM_BADGE_META.map(({ key, label, icon: Icon }) => (
            <div key={key} className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="flex items-center gap-3">
                <Icon className="h-4 w-4 text-primary" />
                <Label htmlFor={`fb-${key}`}>{label}</Label>
              </div>
              <Switch
                id={`fb-${key}`}
                checked={forumBadges[key] !== false}
                onCheckedChange={(v) => toggleForumBadge(key, v)}
                data-testid={`settings-forum-badge-${key}`}
              />
            </div>
          ))}
        </div>

        {forumBadges.achievements !== false && achievements.length > 0 && (
          <div className="mt-4 rounded-md border border-border p-3">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><Trophy className="h-4 w-4 text-primary" /> Achievements shown on forum</p>
            <div className="space-y-2">
              {achievements.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm">{a.label}</span>
                  <Switch
                    checked={!hiddenAch.includes(a.id)}
                    onCheckedChange={(v) => toggleAchievementVisible(a.id, v)}
                    data-testid={`settings-achievement-${a.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="border-border bg-card p-5">
        <h2 className="mb-4 font-semibold">Account</h2>
        <Button variant="destructive" onClick={handleLogout} data-testid="settings-logout-button">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
      </Card>
    </div>
  );
}
